import moment from 'moment';

import { truncateTextWithEllipsis } from '@utilities';
import {
  BrandDefense,
  BrandDefenseFilled,
  BrandSellIn,
  BrandSellInFilled,
  CategoryReview,
  CategoryReviewFilled,
  DiagnosingGrowth,
  DiagnosingGrowthFilled,
  Incrementality,
  IncrementalityFilled,
  WhosMyShopper,
  WhosMyShopperFilled,
} from '@utilities/icons';

const PILL_DATE_FORMAT = 'MM/DD/YYYY';

export const getTileIcon = ({ fastTrackId }) => {
  switch (fastTrackId) {
    case 'brand-defense':
      return {
        filledIcon: <BrandDefenseFilled />,
        icon: <BrandDefense />,
      };
    case 'brand-sell-in':
      return {
        filledIcon: <BrandSellInFilled />,
        icon: <BrandSellIn />,
      };
    case 'category-review':
      return {
        filledIcon: <CategoryReviewFilled />,
        icon: <CategoryReview />,
      };
    case 'diagnosing-growth':
      return {
        filledIcon: <DiagnosingGrowthFilled />,
        icon: <DiagnosingGrowth />,
      };
    case 'incrementality':
      return {
        filledIcon: <IncrementalityFilled />,
        icon: <Incrementality />,
      };
    case 'who-is-my-shopper':
      return {
        filledIcon: <WhosMyShopperFilled />,
        icon: <WhosMyShopper />,
      };
    default:
      return {
        filledIcon: <BrandDefenseFilled />,
        icon: <BrandDefense />,
      };
  }
};

/**
 * transformPromptSelections
 * @param {Object[]|String[]} options.answers
 * @param {Object} options.prompt
 * @return {PromptAnswer[]}
 */
export const transformPromptSelections = ({ answers, prompt }) => {
  if (!answers || answers.length === 0) return [];
  const isDateRange = prompt.promptKey === 'date_range';

  if (isDateRange) {
    return [
      {
        attributeId: prompt.attributes[0]?.id,
        field: prompt.promptKey,
        values: answers,
      },
    ];
  }

  const defaultField = prompt.attributes?.[0]?.field;
  const promptAnswers = [];

  answers.forEach((answer) => {
    const attributeId =
      prompt.attributes?.find(({ id }) => answer.attributeId === id)?.id || defaultField;

    const existingAnswer = promptAnswers.find(
      ({ attributeId: existingAttributeId }) => existingAttributeId === attributeId
    );
    if (existingAnswer) {
      existingAnswer.values.push(answer);
    } else {
      promptAnswers.push({
        attributeId,
        field: prompt.promptKey,
        values: [answer],
      });
    }
  });

  return promptAnswers;
};

/**
 * gets value for pill based on current prompt and answers
 * @param {Object} options
 * @param {Number} options.firstAnswerMaxLength
 * @param {Object} options.prompt
 * @param {PromptAnswer[]} options.promptAnswers group.promptAnswers
 * @return {String}
 */
export const getPromptPillValue = ({ firstAnswerMaxLength = 0, prompt, promptAnswers = [] }) => {
  const answers = promptAnswers
    .filter(({ field }) => field === prompt.id)
    .map(({ values }) => values)
    .flat(1);

  if (answers.length === 0) return prompt?.emptyValue;

  const isDateRange = prompt.promptKey === 'date_range';

  if (isDateRange) {
    if (answers.length === 2 && typeof answers[0] === 'string') {
      return [answers[0], answers[1]]
        .map((value) => moment(value))
        .sort((a, b) => a.diff(b)) // values not guaranteed to be in order, sort is necessary
        .map((date) => date.format(PILL_DATE_FORMAT))
        .join(' to ');
    }

    return answers[0]?.date_range
      ?.map((value) => moment(value).format(PILL_DATE_FORMAT))
      .join(' to ');
  }

  const firstAnswerAttribute = prompt?.attributes?.find(
    (attribute) => attribute?.field === answers[0].attributeId
  );

  const firstAnswerName = firstAnswerMaxLength
    ? truncateTextWithEllipsis({
        length: answers.length > 1 ? firstAnswerMaxLength : firstAnswerMaxLength + 10,
        text: answers[0].name,
      })
    : answers[0].name;

  const firstAnswer =
    firstAnswerAttribute?.name && prompt?.isPrefixIncluded
      ? `${firstAnswerAttribute?.name}: ${firstAnswerName}`
      : firstAnswerName;

  if (answers.length > 1) {
    return `${firstAnswer} ${prompt.isSortEnabled ? 'then' : 'or'} ${answers.length - 1} more`;
  }
  return firstAnswer;
};

/**
 * gets tooltip content for pill based on current prompt and answers
 * @param {Object} options.prompt
 * @param {PromptAnswer[]} options.promptAnswers group.promptAnswers
 * @return {String}
 */
export const getPromptPillTooltip = ({ prompt, promptAnswers = [] }) => {
  const answers = promptAnswers
    .filter(({ field }) => field === prompt.id)
    .map(({ values }) => values)
    .flat(1);
  const isDateRange = prompt.promptKey === 'date_range';

  if (answers.length <= 1 || isDateRange) return null;

  return answers.map((value, index) => {
    return (
      <div key={`selection-${index}`}>
        <small>
          {getPromptPillValue({
            prompt,
            promptAnswers: [{ field: prompt.id, values: [value] }],
          })}
        </small>
      </div>
    );
  });
};
